import React, { useState, useEffect, useCallback } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { StaticImageData } from 'next/image'
import {
  RequirementText,
  Span,
  InfoWrapper,
  DesktopHeroWrapper,
  PricePerUnit,
  Spacer,
  CtaButtonDisabled,
} from './Hero.styles'
import {
  ContentContainer,
  MobileWrapper,
  DesktopWrapper,
} from 'features/components/ProductDetailHero/ProductDetailHero.styles'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { HeroImage } from 'features/components/ProductDetailHero/components/HeroImage'
import { ProductFeatures } from 'features/components/ProductDetailHero/components/ProductFeatures'
import { HeroHeader } from 'features/components/ProductDetailHero/components/HeroHeader'
import { InformationCollapse } from 'features/components/ProductDetailHero/components/InformationCollapse'
import { ProductDetailHero } from 'features/components/ProductDetailHero'
import { RecipeType } from 'features/components/ProductDetailHero/ProductDetailHero'
import { RecipeSelection } from 'features/components/ProductDetailHero/components/RecipeSelection/RecipeSelection'
import { ItemCounter } from 'features/components/ProductDetailHero/components/ItemCounter/ItemCounter'
import { Box } from 'components/Box'
import { CtaButton } from 'components/CtaButton'
import { FetchProductDetail } from 'features/ECommerce/api/queries/useFetchProductDetail'
import { FetchShoppingCart } from 'features/ECommerce/api/queries/useFetchShoppingCart'
import Trans from 'next-translate/Trans'

type HeroProps = {
  recipe: RecipeType
  thumbnail: StaticImageData
  product: FetchProductDetail.Response
  onAdd: (productId: string, quantity: number, productName: string) => void
  shoppingCart: FetchShoppingCart.Response | undefined
}

const countPacksFromCart = (cart?: FetchShoppingCart.Response): number => {
  if (!cart?.items) {
    return 0
  }
  const recipePacks = cart.items.filter((item) => item.slug.includes('5-lb'))
  const total = recipePacks.reduce((sum, recipe) => sum + (recipe.quantity || 0), 0)
  return total
}

const countPacksRemaining = (packsInCart: number) => {
  if (!packsInCart) {
    return 6
  }
  
  const itemsInBox = packsInCart % 6

  if (!itemsInBox) {
    return 0
  } else {
    return 6 - itemsInBox
  }
}

export const Hero = ({ recipe, thumbnail, product, onAdd, shoppingCart }: HeroProps) => {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)
  const packsInCart = countPacksFromCart(shoppingCart)
  const [packsRemaining, setPacksRemaining] = useState(countPacksRemaining(packsInCart))
  const pricePerUnit = product.price.total / 100
  
  const infoList = [
    {
      label: t(`recipes:${recipe}.hero.ingredients.label`),
      description: t(`recipes:${recipe}.hero.ingredients.description`),
    },
    {
      label: t(`recipes:${recipe}.hero.nutrition.label`),
      description: t(`recipes:${recipe}.hero.nutrition.description`),
    },
    {
      label: t('products:fivePound.benefits'),
      description: t('recipes:individual.hero.benefits.description'),
    },
  ]

  const handleAdd = () => {
    setQuantity((prev) => prev + 1)
  }

  const handleRemove = () => {
    if (quantity > 0) {
      setQuantity((prev) => prev - 1)
    }
  }

  const handleAddToCart = () => {
    onAdd(product.productId, quantity, product.name)
    setQuantity(1)
  }

  useEffect(()=> {
    const packsInCart = countPacksFromCart(shoppingCart)
    const newPacksRemaining = countPacksRemaining(packsInCart)
    setPacksRemaining(newPacksRemaining)
  }, [shoppingCart])

  return (
    <ProductDetailHero recipe={recipe}>
      <DesktopHeroWrapper>
        <HeroImage thumbnail={thumbnail} alt={t(`recipes:${recipe}.hero.img`)} />
      </DesktopHeroWrapper>
      <ContentContainer>
        <Stack
          display="flex"
          direction="column"
          alignItems="center"
          justifyContent="flex-start"
          gap={{ '@initial': '3', '@bp3': '7' }}
        >
          <MobileWrapper>
            <RecipeSelection recipe={recipe} />
          </MobileWrapper>
          <HeroHeader
            title={t(`recipes:${recipe}.hero.title`)}
            subtitle={t(`recipes:${recipe}.hero.subtitle`)}
          />
          <MobileWrapper>
            <HeroImage thumbnail={thumbnail} alt={t(`recipes:${recipe}.hero.img`)} />
          </MobileWrapper>
          <DesktopWrapper>
            <ProductFeatures recipe={recipe} />
          </DesktopWrapper>
        </Stack>
        <DesktopWrapper>
          <RecipeSelection recipe={recipe} />
        </DesktopWrapper>
        <PricePerUnit size={{'@intial': 'body', '@bp3': 'bodyLarge'}} color="quaternary-200">
          ${pricePerUnit} / 5 lb Patty
        </PricePerUnit>
        <ItemCounter recipe={recipe} counter={quantity} onAdd={handleAdd} onRemove={handleRemove} />
        {quantity === 0 ? (
          <CtaButtonDisabled recipe={recipe} width="regular">
            {t('products:fivePound.cta')}
          </CtaButtonDisabled>
        ) : (
          <CtaButton onClick={handleAddToCart} type="primary" width="regular">
            {t('products:fivePound.cta')}
          </CtaButton>
        )}
        {packsRemaining ? (
          <Text
            css={{ marginBottom: '$4', marginTop: '$2', maxWidth: '300px', '@bp3': { maxWidth: '100%' } }}
            align="center"
            color="primary-500"
            size={{'@intial': 'body', '@bp3': 'bodyLarge'}}
          >
            Add <Span>{packsRemaining}</Span> more patties to meet min. shipping requirements.
          </Text>
        ) : (
          <Spacer />
        )}
        <MobileWrapper>
          <ProductFeatures recipe={recipe} />
        </MobileWrapper>
        <Box css={{ padding: '0px 8px' }}>
          <RequirementText recipe={recipe}>
            <Trans i18nKey='products:fivePound.shippingDisclaimer' components={{br: <br/>}} />
          </RequirementText>
        </Box>
        <InfoWrapper>
          <InformationCollapse infoList={infoList} />
        </InfoWrapper>
      </ContentContainer>
    </ProductDetailHero>
  )
}

Hero.displayName = 'Hero'
